import { useCallback, useEffect, useRef, useState } from 'react';

import { setContext } from '@sentry/browser';
import _startCase from 'lodash/startCase';
import { match } from 'ts-pattern';

import { Permissions } from '@cloud-wave/neon-common-lib';

import { useConfigContext } from 'lib/core/config';
import { useAuthContext } from 'lib/core/context/AuthProvider';
import { getDefaultBrandImageUrl, getFileUrl } from 'lib/core/utils';

import { usePermissionsContext } from 'lib/common/contexts/PermissionsContext';

import CONNECTION_TYPES from 'lib/common/constants/connectionType';
import CONTACT_STATES from 'lib/common/constants/contactStates';
import CONTACT_TYPES from 'lib/common/constants/contactTypes';
import { ImageType } from 'lib/common/constants/imageBlockFields';

import requestNotificationPermission from 'lib/common/utils/requestNotificationPermission';

import { LogEvents, logger } from '../../components/LoggerController';
import { useAgentContext } from '../AgentContext';
import { usePreferencesContext } from '../PreferencesContext';
import { useRelatedCallsContext } from '../RelatedCallsContext/Context';
import { useRequirementsContext } from '../RequirementsContext';
import Context from './Context';
import { TTaskAction, TaskAction } from './constants/taskAction';
import useAgentActions from './hooks/useAgentActions';
import useCallActions from './hooks/useCallActions';
import useChatActions from './hooks/useChatActions';
import useContactHandlers from './hooks/useContactHandlers';
import useInitialSetup from './hooks/useInitialSetup';
import useSetTaskContent from './hooks/useSetTaskContent';
import useTaskActions from './hooks/useTaskActions';
import useTaskRefresh from './hooks/useTaskRefresh';
import useTaskUtils from './hooks/useTaskUtils';
import useTransferActions from './hooks/useTransferActions';
import { TAnyTask } from './types/ContactContext';
import TCustomerProfile from './types/CustomerProfile';
import { TTaskEvent } from './types/TaskEvent';
import { setDraftTasks } from './utils/draftTasksStorage';
import getUpdatedTask from './utils/getUpdatedTask';
import handleContactAutoAnswer from './utils/handleContactAutoAnswer';
import resolveCustomerProfile from './utils/resolveCustomerProfile';
import taskBuilder from './utils/taskBuilder';
import handleTaskEvent from './utils/taskEvent/handleTaskEvent';
import isOutdatedTaskEvent from './utils/taskEvent/isOutdatedTaskEvent';

function ContactProvider({ children }) {
  const { hasPermission } = usePermissionsContext();
  const { isPermissionGranted } = useRequirementsContext();
  const { config } = useConfigContext();
  const {
    state: { incomingChatNotificationsEnabled },
  } = usePreferencesContext();
  const { fetch_ } = useAuthContext();
  const {
    actions: { handleRelatedCalls },
  } = useRelatedCallsContext();

  const icon =
    config && config.BRAND && config.BRAND.favicon && config.BRAND.favicon.fileKey
      ? getFileUrl(config.BRAND?.favicon.fileKey, config)
      : getDefaultBrandImageUrl(ImageType.FAVICON);
  const [tasks, setTasksState] = useState<TAnyTask[]>([]);
  const [selectedTaskId, setSelectedTaskIdState] = useState<string | undefined>();
  const [lastDialedNumber, setLastDialedNumber] = useState<string | undefined>('');
  const [initialisingTasks, setInitialisingTasks] = useState(true);
  const tasksEventRef = useRef<TTaskEvent[]>([]);
  const incomingChatNotificationsEnabledRef = useRef(incomingChatNotificationsEnabled);
  const hasMicrophonePermission = isPermissionGranted('microphone');
  const hasMicrophonePermissionRef = useRef(hasMicrophonePermission);
  const { agent } = useAgentContext();

  // This is used to make sure we're not running into stale closure where matchNewContact which is used inside an event
  // listener was getting a stale version of microphone permission.
  // This is temporary and would be improved as part of overhauling task handling
  useEffect(() => {
    hasMicrophonePermissionRef.current = hasMicrophonePermission;
  }, [hasMicrophonePermission]);
  useEffect(() => {
    incomingChatNotificationsEnabledRef.current = incomingChatNotificationsEnabled;
  }, [incomingChatNotificationsEnabled]);

  // This ref is used to provide a static "latest" reference to the task list, mostly
  // for use in connect callbacks due to stale references. Never change this outside the setTasks function
  const taskListRef: { current: TAnyTask[] } = useRef(tasks);
  const activeTaskIdRef: { current?: string } = useRef(selectedTaskId);

  const setTasks = (tasks: TAnyTask[]) => {
    taskListRef.current = tasks;
    setTasksState(tasks);

    setContext('tasks', taskBuilder.reportableTasks(tasks));
  };

  const setTask = ({ task, action }: { task: TAnyTask; action: TTaskAction }) => {
    setTasksState((prevTasks) => {
      const newTasks = match(action)
        .with(TaskAction.CREATE, () => [task, ...prevTasks])
        .with(TaskAction.UPDATE, () =>
          prevTasks.map((prevTask) => (prevTask.taskId === task?.taskId ? { ...prevTask, ...task } : prevTask))
        )
        .with(TaskAction.DELETE, () => prevTasks.filter((prevTask) => prevTask.taskId !== task?.taskId))
        .exhaustive();
      taskListRef.current = newTasks;
      return newTasks;
    });

    setContext('tasks', taskBuilder.reportableTasks(tasks));
  };

  const setUnreadMessage = ({ taskId, unreadMessage }: { taskId?: string; unreadMessage: boolean }) => {
    if (!taskId) {
      return;
    }

    return handleContactChange({ taskId, unreadMessage });
  };

  const setSelectedTaskId = (selectedTaskId?: string) => {
    const selectedTask = getTasks().find((task) => task.taskId === selectedTaskId);

    if (selectedTaskId === activeTaskIdRef.current || (selectedTaskId && !selectedTask)) {
      return;
    }

    activeTaskIdRef.current = selectedTaskId;
    setSelectedTaskIdState(selectedTaskId);

    if (!selectedTask || selectedTask.type !== CONTACT_TYPES.CHAT) {
      return;
    }

    setUnreadMessage({ taskId: selectedTaskId, unreadMessage: false });
  };

  const getTasks = () => taskListRef.current;
  const getSelectedTaskId = () => activeTaskIdRef.current;

  const handleContactChange = useCallback(
    async (changes) => {
      tasksEventRef.current = handleTaskEvent({ changes, tasksEventRef });

      try {
        const newOrUpdatedTask = await getUpdatedTask({
          ...changes,
          getTasks,
          getSelectedTaskId,
          handleContactChange,
          hasPermission,
          icon,
          incomingChatNotificationsEnabledRef,
        });

        const isOutdatedEvent = isOutdatedTaskEvent({ tasksEventRef, changes });

        if (isOutdatedEvent || !newOrUpdatedTask) {
          logger.info(LogEvents.TASK_EVENT.DROPPED, changes);
          return;
        }

        // This function handles related calls logic, which a softphone only, single customer functionality
        // It links consecutive calls with a contact attribute, if opted in
        // NEON-5434
        handleRelatedCalls({ changes, task: newOrUpdatedTask.task });

        logger.info(LogEvents.TASK_EVENT.PROCESSED, changes);
        setTask(newOrUpdatedTask);
      } catch {}
    },
    [tasks]
  );

  useEffect(() => {
    const selectedTask = getSelectedTask();

    if (selectedTask?.status === CONTACT_STATES.DRAFT) {
      return;
    }

    (window as any).getConnect().core.viewContact(selectedTask?.taskId);
  }, [selectedTaskId]);

  const hasCustomerProfilesPermission = hasPermission({
    type: 'tenant',
    permission: Permissions.CUSTOMER_PROFILES,
  });

  const hasMissedCallPermission = hasPermission({
    type: 'tenant',
    permission: Permissions.AGENT_MISSED_CALL,
  });

  const setTaskContent = useSetTaskContent({ setTask, getTasks });

  const { makeOutboundCall } = useAgentActions({ tasks, handleContactChange });
  const {
    rejectTask,
    acceptTask,
    endInitialConnection,
    removeTask,
    hangupAgent,
    onJoinConference,
    onSwapConferenceCall,
    addDraftTask,
    removeDraftTask,
    bargeInTask,
    monitorTask,
    pauseTask,
    resumeTask,
  } = useTaskActions({ tasks, handleContactChange, setSelectedTaskId, setTask });
  const {
    holdCall,
    resumeCall,
    holdConferenceConnection,
    resumeConferenceConnection,
    endConferenceConnection,
    muteConnection,
    unmuteConnection,
  } = useCallActions({
    tasks,
    handleContactChange,
  });
  const { onTransferToQueueOrAgent, onTransferToExternalNumber } = useTransferActions({
    tasks,
    handleContactChange,
  });
  const { sendMessage, sendTypingEvent, getChatTranscripts, sendAttachment, downloadAttachment } = useChatActions(
    tasks,
    handleContactChange,
    selectedTaskId
  );

  const searchForProfile = (contact) => {
    return resolveCustomerProfile({
      contact,
      handleContactChange,
      config,
      fetch_,
      hasCustomerProfilesPermission,
    });
  };

  const {
    getActiveCallTask,
    getSelectedTask,
    onActiveCall,
    hasConnectingTask,
    hasConnectingInboundTask,
    hasConnectingInboundVoiceTask,
    hasConnectingVoiceTask,
    isCallInACW,
    hasPreviewDialingTask,
  } = useTaskUtils(tasks, selectedTaskId);

  const matchNewContact = async (contact: connect.Contact) => {
    await handleContactChange({ contact, status: CONTACT_STATES.CONNECTING });

    handleContactAutoAnswer(contact, { hasMicrophonePermission: hasMicrophonePermissionRef.current, agent });

    searchForProfile(contact);

    const newTask = getTasks().find((task) => task.taskId === contact.contactId);

    if (!newTask) {
      return;
    }

    const isOutbound = newTask.connectionType === CONNECTION_TYPES.OUTBOUND;

    logger.info(LogEvents.TASK[isOutbound ? 'OUTBOUND' : 'INCOMING'], { contactId: newTask.contact.contactId });

    await requestNotificationPermission();

    if (document.hasFocus() || isOutbound) {
      return;
    }

    try {
      new Notification(`Incoming ${_startCase(newTask.type.toLowerCase())}`, {
        body: newTask.connectionValue,
        icon: `${window.location.origin}/android-chrome-192x192.png`,
      });
    } catch {}
  };

  useInitialSetup({
    matchNewContact,
    getTasks,
    setTasks,
    handleContactChange,
    getSelectedTaskId,
    setInitialisingTasks,
  });

  useContactHandlers({
    matchNewContact,
    handleContactChange,
    fetch_,
    config,
    getTasks,
  });

  useTaskRefresh({
    getTasks,
    handleContactChange,
    fetch_,
    config,
  });

  useEffect(() => {
    setDraftTasks(tasks);

    // This hook is run when the currently selected task is cleared, to select the first task in the list
    if (!selectedTaskId || tasks.some((task) => task.taskId === selectedTaskId)) {
      return;
    }

    setSelectedTaskId(tasks[0]?.taskId);
  }, [tasks]);

  const deleteExistingCustomerProfile = (taskId: string) =>
    handleContactChange({
      contact: tasks.find((task) => task.taskId === taskId)?.contact,
      profile: null,
    });

  const matchExistingCustomerProfile = ({ profile, taskId }: { profile?: TCustomerProfile; taskId: string }) => {
    const contact = tasks.find((task) => task.taskId === taskId)?.contact;

    if (!contact) {
      return;
    }

    if (!profile) {
      return searchForProfile(contact);
    }

    return handleContactChange({ contact, profile });
  };

  return (
    <Context.Provider
      value={{
        actions: {
          setSelectedTaskId,
          matchExistingCustomerProfile,
          getActiveCallTask,
          deleteExistingCustomerProfile,
          setLastDialedNumber,
          setTaskContent,
          getSelectedTask,
          getSelectedTaskId,

          // agent actions
          makeOutboundCall,

          // task actions
          rejectTask,
          acceptTask,
          endInitialConnection,
          removeTask,
          hangupAgent,
          onJoinConference,
          onSwapConferenceCall,
          addDraftTask,
          removeDraftTask,
          bargeInTask,
          monitorTask,
          pauseTask,
          resumeTask,
          forceClearAllTasks: () => setTasks([]),

          // call actions
          holdCall,
          resumeCall,
          onTransferToQueueOrAgent,
          onTransferToExternalNumber,
          holdConferenceConnection,
          resumeConferenceConnection,
          endConferenceConnection,
          muteConnection,
          unmuteConnection,

          // chat actions
          sendMessage,
          sendAttachment,
          downloadAttachment,
          sendTypingEvent,
          setUnreadMessage,
          getChatTranscripts,
        },
        state: {
          tasks,
          selectedTaskId,
          onActiveCall,
          lastDialedNumber,
          hasPreviewDialingTask,
          hasConnectingTask,
          hasConnectingInboundTask,
          hasConnectingInboundVoiceTask,
          hasConnectingVoiceTask,
          isCallInACW,
          initialisingTasks,
        },
      }}
    >
      {children}
    </Context.Provider>
  );
}

export default ContactProvider;
